import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { AreaCode } from '@ebalance/proto/bfftypes/v1/area_pb'
import { Side } from '@ebalance/proto/bfftypes/v1/side_pb'
import { SupplyCategory } from '@ebalance/proto/bfftypes/v1/supply_category_pb'
import type { Date as ProtoDate } from '@ebalance/proto/google/type/date_pb'

import { getLocalStorageItem, setLocalStorageItem } from '~/libs/localStorage'
import { toProtoDate } from '~/models/dateTime'
import type { DisplayUnit } from '~/pages/Supply48Data/models/displayUnit'
import {
  SUPPLY_48_DATA_FILTERS_STORAGE_KEY,
  type Supply48DataFilters,
  supply48DataFiltersCodec,
} from '~/pages/Supply48Data/models/persistedFilters'

/**
 * §10: フィルタ state + withDirtyGuard を担う hook。
 *
 * 対象日は AC-S4D-002 で単日固定 (日めくりで前日 / 翌日へ 1 日ステップ) のため、
 * 期間 (from / to) ではなく 1 つの targetDate で保持する。
 *
 * withDirtyGuard はここで管理し、pendingFilterChange callback と
 * 確認ダイアログ表示フラグを返す。
 */
export type Supply48FiltersState = {
  buySell: Side
  category: SupplyCategory
  selectedArea: AreaCode
  /** 対象日 (単日固定)。 */
  targetDate: Date
  /** 表示単位 (AC-S4D-037。 保存値は常に kWh/30min で、 切替は表示だけに効く)。 */
  displayUnit: DisplayUnit
  /** 変換済み proto Date (= 一覧取得の date_from / date_to 双方に使う) */
  protoTargetDate: ProtoDate
  // ハンドラ
  handleBuySellChange: (v: Side) => void
  handleCategoryChange: (v: SupplyCategory) => void
  handleAreaChange: (v: AreaCode) => void
  handleTargetDateChange: (v: Date) => void
  handleDisplayUnitChange: (v: DisplayUnit) => void
  /**
   * dirty 行がある場合に確認ダイアログを表示してからフィルタを変更する guard。
   * pendingFilterChange が non-null のとき確認ダイアログを表示する。
   */
  withDirtyGuard: (applyChange: () => void) => void
  pendingFilterChange: (() => void) | null
  /** 確認ダイアログで OK を押したときに呼ぶ */
  confirmFilterChange: () => void
  cancelFilterChange: () => void
}

type Props = {
  availableAreas: AreaCode[]
  hasDirtyRows: boolean
  /** フィルタ変更確認後に editState をクリアする callback */
  onClearEditState: () => void
  /**
   * 初期フィルタ seed (= 取引契約一覧「48点データへ」導線のクエリパラメータ由来)。
   * 初回マウント時の useState 初期値としてのみ使う (= 以降のユーザー操作は上書きしない)。
   * 未指定の項目は従来の既定 (buySell=SELL / area=UNSPECIFIED→先頭 / date=今日) に従う。
   */
  initialBuySell?: Side | undefined
  initialArea?: AreaCode | undefined
  initialDate?: Date | undefined
}

export function useSupply48Filters({
  availableAreas,
  hasDirtyRows,
  onClearEditState,
  initialBuySell,
  initialArea,
  initialDate,
}: Props): Supply48FiltersState {
  // 復元値は mount 時 1 度だけ読む。 **URL seed > localStorage > 既定** の優先順位で
  // 初期値を決める (AC-SC-032 の NOTE が挙げる「URL 指定があれば URL 優先、 無ければ
  // localStorage 復元」の形)。 送り側が明示指定した値を保存値で上書きしない。
  const [restored] = useState<Supply48DataFilters>(() =>
    supply48DataFiltersCodec.deserialize(
      getLocalStorageItem(SUPPLY_48_DATA_FILTERS_STORAGE_KEY),
    ),
  )

  const [buySell, setBuySell] = useState<Side>(
    initialBuySell ?? restored.buySell,
  )
  const [category, setCategory] = useState<SupplyCategory>(restored.category)
  const [selectedArea, setSelectedArea] = useState<AreaCode>(
    initialArea ?? restored.selectedArea,
  )
  // 対象日は常に単日 (AC-S4D-002)。
  const [targetDate, setTargetDate] = useState<Date>(
    () => initialDate ?? restored.targetDate,
  )
  const [displayUnit, setDisplayUnit] = useState<DisplayUnit>(
    restored.displayUnit,
  )
  const [pendingFilterChange, setPendingFilterChange] = useState<
    (() => void) | null
  >(null)

  // 選択エリアが availableAreas に含まれなければ先頭に初期化
  useEffect(() => {
    if (availableAreas.length > 0 && !availableAreas.includes(selectedArea)) {
      setSelectedArea(availableAreas[0])
    }
  }, [availableAreas, selectedArea])

  // 保持の書き戻し (AC-SC-031)。
  //
  // **書く契機は利用者の操作だけ** (ハンドラが `markUserChanged` で印を付ける)。
  // state の差分で判定すると、 上の「選択エリアが availableAreas に無ければ先頭へ」
  // effect が**取得完了のたびに保存を誘発する**。 既定値を書いてしまうと、
  // ServerClockGate の待ち上限経路で端末時計由来の初期対象日が localStorage に載り、
  // offset 確定後の subtree 作り直しでもサーバー時計で再導出できなくなる
  // (`~/hooks/usePersistedFilters` の godoc と同じ理由)。
  //
  // 一度でも操作されたら以後は自動補正の結果も保存する (候補外エリアを先頭へ寄せた
  // 結果を保存しないと、 再訪のたびに同じ補正をやり直すことになる)。
  const hasUserChangedRef = useRef(false)
  const markUserChanged = useCallback(() => {
    hasUserChangedRef.current = true
  }, [])
  useEffect(() => {
    if (!hasUserChangedRef.current) return
    setLocalStorageItem(
      SUPPLY_48_DATA_FILTERS_STORAGE_KEY,
      supply48DataFiltersCodec.serialize({
        buySell,
        category,
        selectedArea,
        targetDate,
        displayUnit,
      }),
    )
  }, [buySell, category, selectedArea, targetDate, displayUnit])

  // proto Date 変換 (単日固定のため date_from / date_to 共通)
  const protoTargetDate = useMemo(() => toProtoDate(targetDate), [targetDate])

  const withDirtyGuard = useCallback(
    (applyChange: () => void) => {
      if (hasDirtyRows) {
        setPendingFilterChange(() => applyChange)
      } else {
        applyChange()
        // フィルタを跨いだ編集内容は保存対象にならないのでクリアする
        onClearEditState()
      }
    },
    [hasDirtyRows, onClearEditState],
  )

  const confirmFilterChange = useCallback(() => {
    if (pendingFilterChange !== null) {
      pendingFilterChange()
      onClearEditState()
      setPendingFilterChange(null)
    }
  }, [pendingFilterChange, onClearEditState])

  const cancelFilterChange = useCallback(() => {
    setPendingFilterChange(null)
  }, [])

  const handleBuySellChange = useCallback(
    (v: Side) => {
      withDirtyGuard(() => {
        markUserChanged()
        setBuySell(v)
        setCategory(SupplyCategory.BILATERAL)
      })
    },
    [withDirtyGuard, markUserChanged],
  )

  const handleCategoryChange = useCallback(
    (v: SupplyCategory) => {
      withDirtyGuard(() => {
        markUserChanged()
        setCategory(v)
      })
    },
    [withDirtyGuard, markUserChanged],
  )

  const handleAreaChange = useCallback(
    (v: AreaCode) => {
      withDirtyGuard(() => {
        markUserChanged()
        setSelectedArea(v)
      })
    },
    [withDirtyGuard, markUserChanged],
  )

  const handleTargetDateChange = useCallback(
    (v: Date) => {
      withDirtyGuard(() => {
        markUserChanged()
        setTargetDate(v)
      })
    },
    [withDirtyGuard, markUserChanged],
  )

  // 表示単位は取得条件を変えず、 保存値も常に kWh/30min 固定 (AC-S4D-037) なので
  // dirty guard は通さない (編集中に単位を切り替えても失われるものが無い)。
  const handleDisplayUnitChange = useCallback(
    (v: DisplayUnit) => {
      markUserChanged()
      setDisplayUnit(v)
    },
    [markUserChanged],
  )

  return {
    buySell,
    category,
    selectedArea,
    targetDate,
    displayUnit,
    protoTargetDate,
    handleBuySellChange,
    handleCategoryChange,
    handleAreaChange,
    handleTargetDateChange,
    handleDisplayUnitChange,
    withDirtyGuard,
    pendingFilterChange,
    confirmFilterChange,
    cancelFilterChange,
  }
}
